import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { announceBillingAccess, isLiveSubscriptionStatus } from '../utils/subscriptionAccess';
import { FiCheck, FiFileText, FiCreditCard, FiStar, FiHelpCircle, FiAlertTriangle } from 'react-icons/fi';
import { graphqlRequest } from '../utils/graphqlRequest';
import { adminPath, ADMIN_ROUTES } from '../constants/adminPortal';
import {
  fetchAllCompanyUsers,
  isWorkforcePerson,
  workforceRoleLabel,
} from './WorkForce/utils/workforcePeople';
import { useAdminPageSearch } from '../utils/useAdminPageSearch';
import BillingPlans from './BillingPlans';
import BillingPayment from './BillingPayment';
import BillingInvoices from './BillingInvoices';
import PlanChangeDrawer from './PlanChangeDrawer';
import '../styles/BillingSubscription.css';

const TABS = [
  { id: 'overview', label: 'Overview', subtitle: 'Your SAS Cleaning Suite subscription at a glance' },
  { id: 'plans', label: 'Plans', subtitle: 'Compare plans and choose what works for your business' },
  { id: 'payment', label: 'Payment & Billing', subtitle: 'Manage payment methods, billing details and contacts' },
  { id: 'invoices', label: 'Invoices', subtitle: 'View and download your billing history' },
];

const GET_BILLING_OVERVIEW = `
  query GetMyBillingOverview {
    getMyBillingOverview
  }
`;

const COMPLETE_REACTIVATION = `
  mutation CompleteBillingReactivation($sessionId: String!) {
    completeBillingReactivation(sessionId: $sessionId)
  }
`;

const GET_ALL_USERS = `
  query GetAllUsers($page: Int, $limit: Int) {
    getAllUser(page: $page, limit: $limit) {
      data { id roles role isActive }
      total
      totalPages
      page
    }
  }
`;

const STATUS_LABELS = {
  active: 'Active',
  trialing: 'Active',
  past_due: 'Payment overdue',
  unpaid: 'Unpaid',
  canceled: 'Cancelled',
  incomplete: 'Incomplete',
  incomplete_expired: 'Expired',
  paused: 'Paused',
};

const DOT_COLORS = ['#5b3fd9', '#3b82f6', '#f59e0b', '#10b981', '#ec4899', '#64748b'];
const DAY_MS = 86400000;

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

const formatMoney = (amount, currency = 'usd', whole = false) =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: currency.toUpperCase(),
    currencyDisplay: 'narrowSymbol',
    ...(whole && Number.isInteger(amount) ? { minimumFractionDigits: 0 } : {}),
  }).format(amount || 0);

const pluralLabel = (label) => (label.endsWith('s') ? label : `${label}s`);

function DaysRing({ daysLeft, totalDays }) {
  const r = 42;
  const circumference = 2 * Math.PI * r;
  const fraction = totalDays > 0 ? Math.min(1, Math.max(0, daysLeft / totalDays)) : 0;
  return (
    <div className="bs-ring">
      <svg viewBox="0 0 100 100" aria-hidden="true">
        <circle cx="50" cy="50" r={r} className="bs-ring-track" />
        <circle
          cx="50"
          cy="50"
          r={r}
          className="bs-ring-value"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - fraction)}
        />
      </svg>
      <div className="bs-ring-label">
        <strong>{daysLeft}</strong>
        <span>days left</span>
      </div>
    </div>
  );
}

export default function BillingSubscription({ billingOnly = false }) {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState(null);
  // Change plan drawer: null = closed, '' = open on the current plan, else a price id.
  const [changingPlan, setChangingPlan] = useState(null);
  // Sub-tab lives in the URL so returning from Stripe's card page lands on Payment & Billing.
  const { get, patch } = useAdminPageSearch();
  const tab = TABS.some((t) => t.id === get('billingTab')) ? get('billingTab') : 'overview';
  const setTab = (id) => patch({ billingTab: id === 'overview' ? '' : id });

  const loadOverview = () =>
    graphqlRequest(GET_BILLING_OVERVIEW)
      .then((res) => setOverview(res?.getMyBillingOverview || null))
      .catch((e) => setError(e?.message || 'Failed to load billing details.'))
      .finally(() => setLoading(false));

  // Back from Stripe Checkout after reactivating: confirm, then let the app leave billing-only mode.
  const reactivateSession = get('reactivate_session');
  const handledReactivation = useRef('');
  useEffect(() => {
    if (!reactivateSession || handledReactivation.current === reactivateSession) return;
    handledReactivation.current = reactivateSession;
    graphqlRequest(COMPLETE_REACTIVATION, { sessionId: reactivateSession })
      .then((res) => {
        if (res?.completeBillingReactivation?.access === 'full') {
          toast.success('Welcome back! Your subscription is active again.');
          announceBillingAccess('full');
        }
        loadOverview();
      })
      .catch((e) => toast.error(e?.message || 'Reactivation was not completed.'))
      .finally(() => patch({ reactivate_session: '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reactivateSession]);

  useEffect(() => {
    loadOverview();
    if (billingOnly) {
      setStaff({}); // staff list isn't available while the subscription has ended
      return;
    }

    fetchAllCompanyUsers(graphqlRequest, GET_ALL_USERS)
      .then((rows) => {
        const counts = {};
        (rows || [])
          .filter((u) => u.isActive !== false && isWorkforcePerson(u))
          .forEach((u) => {
            const label = workforceRoleLabel(u.roles, u.role);
            counts[label] = (counts[label] || 0) + 1;
          });
        setStaff(counts);
      })
      .catch(() => setStaff({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Card / auto-renew changes on Payment & Billing affect the Overview numbers.
  useEffect(() => {
    if (tab === 'overview' && overview) loadOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const staffRows = useMemo(
    () => Object.entries(staff || {}).sort((a, b) => b[1] - a[1]),
    [staff],
  );
  const staffUsed = staffRows.reduce((sum, [, n]) => sum + n, 0);

  if (loading) return <div className="bs-page"><p className="bs-muted">Loading billing…</p></div>;
  if (error || !overview) {
    return (
      <div className="bs-page">
        <div className="bs-banner bs-banner--warn">
          <FiAlertTriangle />
          <div><strong>Billing unavailable</strong><p>{error || 'No subscription found.'}</p></div>
        </div>
      </div>
    );
  }

  const { plan, status, nextInvoice, paymentMethod } = overview;
  const isTrial = status === 'trialing' && overview.trialEnd;
  const isLive = ['active', 'trialing'].includes(status);
  const ended = !isLiveSubscriptionStatus(status);
  const endIso = isTrial ? overview.trialEnd : overview.currentPeriodEnd;
  const daysLeft = endIso ? Math.max(0, Math.ceil((new Date(endIso) - Date.now()) / DAY_MS)) : 0;
  const totalDays = isTrial
    ? Math.round((new Date(overview.trialEnd) - new Date(overview.trialStart)) / DAY_MS)
    : plan.interval === 'year' ? 365 : 30;
  const staffLimit = typeof plan.totalStaff === 'number' ? plan.totalStaff : null;
  const staffPct = staffLimit ? Math.round((staffUsed / staffLimit) * 100) : null;

  return (
    <div className="bs-page">
      <header className="bs-header">
        <div>
          <h2>Billing &amp; Subscription</h2>
          <p className="bs-muted">{TABS.find((t) => t.id === tab).subtitle}</p>
        </div>
        <span className={`bs-pill ${isLive ? 'bs-pill--ok' : 'bs-pill--warn'}`}>
          <span className="bs-dot" />
          {isLive ? 'Subscription active' : STATUS_LABELS[status] || status}
        </span>
      </header>

      <nav className="bs-tabs" role="tablist" aria-label="Billing sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={tab === t.id ? 'active' : ''}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            {t.id === 'invoices' && overview.invoiceCount > 0 && (
              <span className="bs-tab-count">{overview.invoiceCount}</span>
            )}
          </button>
        ))}
      </nav>

      {tab === 'plans' ? (
        <BillingPlans
          overview={overview}
          formatMoney={formatMoney}
          ended={ended}
          billingOnly={billingOnly}
          onChangePlan={(priceId) => setChangingPlan(priceId)}
        />
      ) : tab === 'payment' ? (
        <BillingPayment overview={overview} formatDate={formatDate} />
      ) : tab === 'invoices' ? (
        <BillingInvoices formatMoney={formatMoney} />
      ) : (
      <>
      {ended ? (
        <div className="bs-banner bs-banner--warn">
          <FiAlertTriangle />
          <div>
            <strong>Subscription ended</strong>
            <p>
              {status === 'unpaid'
                ? 'Your last payment failed. Pay the outstanding invoice under Invoices, or choose a plan to start again.'
                : 'Choose a plan to reactivate. Your data is kept and comes back as soon as you do.'}
            </p>
          </div>
          <button type="button" className="bs-btn bs-btn--primary bs-banner-action" onClick={() => setTab('plans')}>
            Reactivate
          </button>
        </div>
      ) : overview.cancelAtPeriodEnd ? (
        <div className="bs-banner bs-banner--warn">
          <FiAlertTriangle />
          <div>
            <strong>Subscription cancelled</strong>
            <p>You have access until {formatDate(endIso)}. It will not renew after that date.</p>
          </div>
        </div>
      ) : status === 'past_due' || status === 'unpaid' ? (
        <div className="bs-banner bs-banner--warn">
          <FiAlertTriangle />
          <div>
            <strong>Payment overdue</strong>
            <p>Your last payment did not go through. Please update your payment method.</p>
          </div>
        </div>
      ) : overview.scheduledChange ? (
        <div className="bs-banner">
          <FiStar />
          <div>
            <strong>Plan change scheduled</strong>
            <p>
              You move to {overview.scheduledChange.name} on {formatDate(overview.scheduledChange.date)}. Nothing is
              charged until then.
            </p>
          </div>
        </div>
      ) : isTrial ? (
        <div className="bs-banner">
          <FiStar />
          <div>
            <strong>{overview.isFoundingMember ? 'Founding Member access is active' : 'Free trial is active'}</strong>
            <p>
              Your {overview.isFoundingMember ? 'six-month ' : ''}free period ends {formatDate(overview.trialEnd)}. Your
              first payment will be charged after that date.
            </p>
          </div>
        </div>
      ) : null}

      <div className="bs-grid">
        <section className="bs-card">
          <div className="bs-plan-head">
            <div>
              <p className="bs-eyebrow">Current plan</p>
              <div className="bs-plan-name">
                <h3>{plan.name}</h3>
                {overview.isFoundingMember && <span className="bs-badge bs-badge--purple">Founding Member</span>}
                <span className={`bs-badge ${isLive ? 'bs-badge--green' : 'bs-badge--amber'}`}>
                  {STATUS_LABELS[status] || status}
                </span>
              </div>
            </div>
            <div className="bs-price">
              <strong>{formatMoney(plan.amount, plan.currency, true)}</strong>
              {plan.interval && <span> / {plan.interval}</span>}
            </div>
          </div>

          <div className="bs-plan-box">
            <div className="bs-plan-box-left">
              <DaysRing daysLeft={daysLeft} totalDays={totalDays} />
              <div>
                <strong>{isTrial ? `Free access ends ${formatDate(overview.trialEnd)}` : `${ended ? 'Ended' : overview.cancelAtPeriodEnd ? 'Ends' : 'Renews'} ${formatDate(endIso)}`}</strong>
                <p className="bs-muted bs-small">Started {formatDate(overview.startedAt)}</p>
                <div className="bs-bar">
                  <span style={{ width: `${totalDays ? Math.min(100, ((totalDays - daysLeft) / totalDays) * 100) : 0}%` }} />
                </div>
              </div>
            </div>
            <dl className="bs-plan-box-right">
              <div>
                <dt>Billing cycle</dt>
                <dd>{plan.interval === 'year' ? 'Yearly' : plan.interval === 'month' ? 'Monthly' : '—'}</dd>
              </div>
              <div>
                <dt>{isTrial ? 'First paid invoice' : 'Next invoice'}</dt>
                <dd>
                  {nextInvoice ? (
                    <>
                      {formatDate(nextInvoice.date)} · {formatMoney(nextInvoice.amount, nextInvoice.currency)}
                      {nextInvoice.tax > 0 && (
                        <span className="bs-muted bs-small"> incl. tax {formatMoney(nextInvoice.tax, nextInvoice.currency)}</span>
                      )}
                    </>
                  ) : '—'}
                </dd>
              </div>
              <div>
                <dt>Payment method</dt>
                <dd>
                  {paymentMethod ? (
                    <><span className="bs-card-brand">{paymentMethod.brand}</span> •••• {paymentMethod.last4}</>
                  ) : 'Not added yet'}
                </dd>
              </div>
            </dl>
          </div>

          {plan.features.length > 0 && (
            <>
              <p className="bs-section-title">Included in your plan</p>
              <ul className="bs-features">
                {plan.features.map((f) => (
                  <li key={f}><span className="bs-check"><FiCheck /></span>{f}</li>
                ))}
              </ul>
            </>
          )}

          <div className="bs-plan-actions">
            <button
              type="button"
              className="bs-btn bs-btn--primary"
              onClick={() => (ended ? setTab('plans') : setChangingPlan(''))}
            >
              {ended ? 'Reactivate' : 'Change plan'}
            </button>
            <button type="button" className="bs-btn" onClick={() => setTab('plans')}>
              View plan features
            </button>
          </div>
        </section>

        <section className="bs-card">
          <div className="bs-plan-head">
            <div>
              <p className="bs-eyebrow">Staff usage</p>
              <h4 className="bs-staff-title">
                {staffLimit ? `${staffUsed} of ${staffLimit} staff used` : `${staffUsed} staff`}
              </h4>
            </div>
            {staffPct !== null && <strong className="bs-staff-pct">{staffPct}%</strong>}
          </div>
          {staffLimit ? (
            <>
              <div className="bs-bar bs-bar--usage">
                <span style={{ width: `${Math.min(100, staffPct)}%` }} />
              </div>
              <div className="bs-usage-meta">
                <span className={staffUsed >= staffLimit ? 'bs-text-danger' : 'bs-text-amber'}>
                  {staffUsed >= staffLimit ? 'Staff limit reached' : `${staffLimit - staffUsed} seats remaining`}
                </span>
                {overview.currentPeriodEnd && !ended && <span className="bs-muted">Renews {formatDate(overview.currentPeriodEnd)}</span>}
              </div>
            </>
          ) : (
            <p className="bs-muted bs-small">{plan.totalStaff === 'unlimited' ? 'Unlimited staff on your plan.' : ''}</p>
          )}
          <ul className="bs-staff-list">
            {staff === null ? (
              <li className="bs-muted">Loading…</li>
            ) : (
              staffRows.map(([label, count], i) => (
                <li key={label}>
                  <span><span className="bs-dot" style={{ background: DOT_COLORS[i % DOT_COLORS.length] }} />{pluralLabel(label)}</span>
                  <strong>{count}</strong>
                </li>
              ))
            )}
          </ul>
          {plan.pricePerStaff ? (
            <p className="bs-note">
              <FiHelpCircle /> Extra staff are <strong>{formatMoney(plan.pricePerStaff, plan.currency)} per user/{plan.interval || 'month'}</strong> on {plan.name}.
            </p>
          ) : null}
        </section>
      </div>

      <div className="bs-stats">
        <div className="bs-card bs-stat">
          <span className="bs-stat-icon bs-stat-icon--purple"><FiFileText /></span>
          <div>
            <p className="bs-muted bs-small">Next invoice</p>
            <strong>{nextInvoice ? formatMoney(nextInvoice.amount, nextInvoice.currency) : '—'}</strong>
            <p className="bs-muted bs-small">{nextInvoice ? formatDate(nextInvoice.date) : 'No upcoming invoice'}</p>
          </div>
        </div>
        <div className="bs-card bs-stat">
          <span className="bs-stat-icon bs-stat-icon--green"><FiCreditCard /></span>
          <div>
            <p className="bs-muted bs-small">Total paid to date</p>
            <strong>{formatMoney(overview.totalPaid, plan.currency)}</strong>
            <p className="bs-muted bs-small">{overview.invoiceCount} invoice{overview.invoiceCount === 1 ? '' : 's'}</p>
          </div>
        </div>
        {overview.isFoundingMember && (
          <div className="bs-card bs-stat">
            <span className="bs-stat-icon bs-stat-icon--amber"><FiStar /></span>
            <div>
              <p className="bs-muted bs-small">Founding Member</p>
              <strong className="bs-stat-text">Launch benefits active</strong>
              <p className="bs-muted bs-small">6 months free · Founder support</p>
            </div>
          </div>
        )}
      </div>

      {!billingOnly && (
        <div className="bs-help">
          <span className="bs-help-icon"><FiHelpCircle /></span>
          <div>
            <strong>Need help choosing the right plan?</strong>
            <p>Our subscription team can walk you through plans, billing or migration.</p>
          </div>
          <button type="button" onClick={() => navigate(adminPath(ADMIN_ROUTES.NEW_TICKET))}>
            Talk to our team
          </button>
        </div>
      )}
      </>
      )}

      {changingPlan !== null && !ended && (
        <PlanChangeDrawer
          overview={overview}
          staffUsed={staffUsed}
          initialPriceId={changingPlan}
          formatMoney={formatMoney}
          formatDate={formatDate}
          onClose={() => setChangingPlan(null)}
          onChanged={(next) => next && setOverview(next)}
          onManageCards={() => {
            setChangingPlan(null);
            setTab('payment');
          }}
          onContactSales={(plan) =>
            navigate(adminPath(ADMIN_ROUTES.NEW_TICKET), {
              state: {
                subject: `${plan.name} plan enquiry`,
                category: 'billing',
                description: `We'd like to discuss the ${plan.name} plan for our business.`,
              },
            })
          }
        />
      )}
    </div>
  );
}
